import { Button } from '@byecode/ui'
import React from 'react'
import styled from 'styled-components'

import type { MarkdownImportSummary } from './types'

interface SummaryDialogProps {
    summary: MarkdownImportSummary
    onClose: () => void
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 0;
`

const CountRow = styled.div`
    display: flex;
    justify-content: space-around;
    text-align: center;
`

const CountItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`

const CountValue = styled.div<{ $tone: 'success' | 'fail' | 'skip' }>`
    font-size: 24px;
    font-weight: 600;
    color: ${props => {
        if (props.$tone === 'success') return 'var(--color-green-600)'
        if (props.$tone === 'fail') return 'var(--color-red-500)'
        return 'var(--color-gray-400)'
    }};
`

const CountLabel = styled.div`
    font-size: var(--font-size-sm);
    color: var(--color-gray-400);
`

const DetailHeader = styled.div`
    font-size: var(--font-size-normal);
    color: var(--color-gray-600);
`

const DetailList = styled.div`
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid var(--color-gray-200);
    border-radius: 6px;
`

const DetailRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-gray-100);

    &:last-child {
        border-bottom: none;
    }
`

const DetailFileName = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--font-size-normal);
    color: var(--color-gray-600);
`

const DetailStatus = styled.span<{ $tone: 'success' | 'fail' | 'skip' }>`
    flex-shrink: 0;
    margin-left: 12px;
    font-size: var(--font-size-sm);
    color: ${props => {
        if (props.$tone === 'success') return 'var(--color-green-600)'
        if (props.$tone === 'fail') return 'var(--color-red-500)'
        return 'var(--color-gray-400)'
    }};
`

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
`

const statusText: Record<MarkdownImportSummary['details'][number]['status'], string> = {
    success: '成功',
    fail: '失败',
    skip: '跳过'
}

export const SummaryDialog: React.FC<SummaryDialogProps> = ({ summary, onClose }) => {
    return (
        <Container>
            <CountRow>
                <CountItem>
                    <CountValue $tone="success">{summary.success}</CountValue>
                    <CountLabel>成功</CountLabel>
                </CountItem>
                <CountItem>
                    <CountValue $tone="fail">{summary.fail}</CountValue>
                    <CountLabel>失败</CountLabel>
                </CountItem>
                <CountItem>
                    <CountValue $tone="skip">{summary.skip}</CountValue>
                    <CountLabel>跳过</CountLabel>
                </CountItem>
            </CountRow>
            {summary.details.length > 0 && (
                <div>
                    <DetailHeader>文件明细</DetailHeader>
                    <DetailList>
                        {summary.details.map((item, index) => (
                            <DetailRow key={`${item.fileName}-${index}`}>
                                <DetailFileName title={item.fileName}>{item.fileName}</DetailFileName>
                                <DetailStatus $tone={item.status} title={item.message}>
                                    {statusText[item.status]}
                                </DetailStatus>
                            </DetailRow>
                        ))}
                    </DetailList>
                </div>
            )}
            <Footer>
                <Button type="primary" onClick={onClose}>
                    完成
                </Button>
            </Footer>
        </Container>
    )
}
